import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";

interface SearchContextType {
  searchQuery: string;
  searchType: string;
  searchBrand: string;
  searchItem: string;
  setSearchQuery: (query: string) => void;
  setSearchType: (type: string) => void;
  setSearchBrand: (brand: string) => void;
  setSearchItem: (item: string) => void;
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBrand, setSearchBrand] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [searchType, setSearchType] = useState<string>(
    t("supplier_sidebar.products")
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize search parameters from URL on mount
  useEffect(() => {
    const brand = searchParams.get("brand");
    const item = searchParams.get("item");
    const query = searchParams.get("query");

    if (brand) {
      setSearchBrand(brand);
    }
    if (item) {
      setSearchItem(item);
    }
    if (query) {
      setSearchQuery(query);
    }
  }, [searchParams]);

  const clearSearch = () => {
    setSearchQuery("");
    setSearchBrand("");
    setSearchItem("");
    setSearchParams({});
  };

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        searchType,
        searchBrand,
        searchItem,
        setSearchQuery,
        setSearchType,
        setSearchBrand,
        setSearchItem,
        clearSearch,
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
}
